import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, API_BASE, formatApiError, fmtCurrency, fmtEur, fmtInt, fmtPct, fmtYears, tokenStore } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import AppLayout from "@/components/AppLayout";
import MonteCarloPanel from "@/components/MonteCarloPanel";
import SensitivityPanel from "@/components/SensitivityPanel";
import { ArrowLeft, Download, FileText, TrendingUp, History, CalendarClock, AlertTriangle, Copy, MessageSquare } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  BarChart, Bar, ReferenceLine, ComposedChart, Area, AreaChart,
} from "recharts";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import { saveOrDownload, useDownloadFolderName } from "@/lib/downloadFolder";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import ChartDescriptionEditor from "@/components/ChartDescriptionEditor";
import ChartDescriptionsDialog from "@/components/ChartDescriptionsDialog";
import { ReportNav, ReportSection, useReportSections } from "@/components/ReportSection";
import { term, termShort, termHelp } from "@/lib/terms";
import HelpButton from "@/components/HelpButton";
import SubpostGrowthTable, { CATEGORY_LABELS } from "@/components/SubpostGrowthTable";
import ClubTour, { TourButton, isTourDisabled, setTourDisabled } from "@/components/ClubTour";
import { applyDiagonalWatermark, hexToRgb } from "@/lib/pdfBranding";
import { BtwStrip } from "@/components/btw/BtwStrip";
import { btwScenarioRows } from "@/lib/btw";

export default function ScenarioDetail() {
  const t = useT();
  const { clubId, scenarioId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const hasAiAccess = user && (user.tier === "TRIAL" || user.tier === "PREMIUM" || user.role === "admin");
  const [data, setData] = useState(null);
  const [computeError, setComputeError] = useState(null);
  const [historicals, setHistoricals] = useState(null); // null = not loaded yet
  const [club, setClub] = useState(null);
  const [showGapDialog, setShowGapDialog] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [showDupDialog, setShowDupDialog] = useState(false);
  const [dupName, setDupName] = useState("");
  const [dupSaving, setDupSaving] = useState(false);
  const [exportDlg, setExportDlg] = useState(null); // {format: 'pdf'|'csv'|'xlsx', filename: string}
  const [tourOpen, setTourOpen] = useState(false);
  const [tourDisabled, setTourDisabledState] = useState(isTourDisabled());
  const startTour = () => { if (tourDisabled) { setTourDisabled(false); setTourDisabledState(false); } setForceOpen(true); setTimeout(() => setTourOpen(true), 150); };
  const tourSteps = useMemo(() => [
    { target: '[data-testid="report-nav"]', title: t("Inhoud van het rapport"), text: t("Het rapport is opgedeeld in Overzicht, Detail en Risico. Klik op een onderdeel om ernaartoe te springen; elk onderdeel kun je in- of uitklappen en die stand wordt onthouden.") },
    { target: '[data-testid="at-a-glance-card"]', title: t("Kernmetrieken"), text: t("In één oogopslag: de structuur van het scenario (investeringen, lening, eigen inbreng, basisomzet en -kosten, belasting, inflatie).") },
    { target: '[data-testid="scenario-summary-block"]', title: t("AI-samenvatting"), text: t("Een leesbare samenvatting van de uitkomsten voor bestuur en leden; komt ook op de voorpagina van de PDF.") },
    { target: '[data-testid="cumulative-cash-flow-chart"]', title: t("Kasstroomtraject"), text: t("De cumulatieve kasstroom door de jaren heen: waar de lijn onder nul duikt, komt de club in de knel; het break-evenpunt is waar hij weer boven nul komt.") },
    { target: '[data-testid="liquidity-buildup"]', title: t("Liquiditeitsopbouw"), text: t("Per jaar de opbouw van kas en bank: exploitatie, investeringen, leningen, reserves en belasting — zo zie je precies waar het geld heen gaat.") },
    { target: '[data-testid="operational-profile-chart"]', title: t("Operationeel profiel"), text: t("Omzet versus kosten en afschrijving in de tijd. Lopen de lijnen naar elkaar toe, dan smelt de exploitatiemarge.") },
    { target: '[data-testid="subpost-growth-card"]', title: t("Gebruikte inflatie per subpost"), text: t("Verantwoording voor de kascommissie: welk percentage per subpost is gebruikt en of dat een eigen percentage of de categorie-standaard was.") },
    { target: '[data-testid="future-schedule"]', title: t("Investeringsplan"), text: t("De geplande investeringen met bedrag, jaar, financiering en afschrijving per jaar.") },
    { target: '[data-testid="monte-carlo-panel"]', title: term("montecarlo"), text: t("Laat het scenario duizenden keren doorrekenen met variërende aannames en zie hoe robuust de uitkomst is.") },
    { target: '[data-testid="scenario-actions"]', title: t("Exports en bewerken"), text: t("Exporteer naar CSV, Excel (met tabblad Inflatie subposten) of PDF; bewerk of dupliceer het scenario om varianten te vergelijken.") },
  ], [t]);
  const folderName = useDownloadFolderName(clubId);
  const reportRef = useRef();
  const [descDlgOpen, setDescDlgOpen] = useState(false);
  // Rapportsecties: inklapbaar met onthouden stand; tijdens PDF-export en rondleiding tijdelijk alles open.
  const REPORT_SECTIONS = useMemo(() => [
    { id: "plans", group: "overzicht", title: t("Investeringsplannen"), short: t("Investeringen") },
    { id: "glance", group: "overzicht", title: t("In één oogopslag & kasstroomtraject"), short: t("Kasstroom") },
    { id: "operational", group: "overzicht", title: t("Operationeel profiel"), short: t("Operationeel") },
    { id: "inflation", group: "detail", title: t("Gebruikte inflatie per subpost"), short: t("Inflatie"), defaultOpen: false },
    { id: "liquidity", group: "detail", title: t("Liquiditeitsopbouw"), short: t("Liquiditeit") },
    { id: "reserves_chart", group: "detail", title: t("Reserves & voorzieningen — opbouw"), short: t("Reserves-grafiek"), defaultOpen: false },
    { id: "reserves", group: "detail", title: t("Saldi van (bestemmings)reserves"), short: t("Reserves"), defaultOpen: false },
    { id: "loans", group: "detail", title: t("Bestaande leningen & schulddekking"), short: t("Leningen"), defaultOpen: false },
    { id: "sensitivity", group: "risico", title: term("tornado"), short: t("Gevoeligheid"), defaultOpen: false },
    { id: "montecarlo", group: "risico", title: term("montecarlo"), short: t("Risicosimulatie"), defaultOpen: false },
  ], [t]);
  const { openMap, toggle: toggleSection, setAll: setAllSections } = useReportSections(REPORT_SECTIONS);
  const [forceOpen, setForceOpen] = useState(false);
  const jumpToSection = (id) => {
    toggleSection(id, true);
    setTimeout(() => document.getElementById(`report-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };
  const withAllOpen = async (fn) => {
    const anyClosed = REPORT_SECTIONS.some((sec) => !openMap[sec.id]);
    setForceOpen(true);
    if (anyClosed) await new Promise((r) => setTimeout(r, 1700));
    try { await fn(); } finally { setForceOpen(false); }
  };

  const loadCompute = () => {
    setComputeError(null);
    api.get(`/scenarios/${scenarioId}/compute`)
      .then(({ data }) => setData(data))
      .catch((e) => {
        const detail = formatApiError(e.response?.data?.detail) || String(e.message || e);
        setComputeError(detail);
        toast.error(detail);
      });
  };

  // Save a chart description or the scenario-level summary to the backend and
  // patch the loaded scenario in place so the preview refreshes immediately
  // without a full recompute.
  const saveScenarioDescription = async ({ chartKey, description, summary }) => {
    const body = {};
    if (chartKey !== undefined) { body.chart_key = chartKey; body.description = description ?? ""; }
    if (summary !== undefined) { body.summary = summary; }
    const { data: updated } = await api.patch(`/scenarios/${scenarioId}/description`, body);
    setData((d) => d ? { ...d, scenario: { ...d.scenario, chart_descriptions: updated.chart_descriptions || {}, scenario_summary: updated.scenario_summary || "" } } : d);
  };

  // Ask Claude Sonnet 5 for a NL scenario summary. Only offered to TRIAL/PREMIUM
  // (admins are auto-PREMIUM). Returns text but does NOT save — user reviews first.
  const generateAiSummary = async () => {
    const { data: r } = await api.post(`/scenarios/${scenarioId}/ai-summary`);
    return r.summary || "";
  };

  useEffect(() => {
    loadCompute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioId]);

  useEffect(() => {
    api.get(`/clubs/${clubId}`).then(({ data }) => setClub(data)).catch(() => setClub(null));
  }, [clubId]);

  useEffect(() => {
    api.get(`/clubs/${clubId}/historicals`)
      .then(({ data }) => setHistoricals(data))
      .catch(() => setHistoricals([]));
  }, [clubId]);

  // Detect years missing between earliest historical and (start_year - 1).
  // Returns null while historicals are still loading so the dialog doesn't
  // race-open with a stale zero-history assumption.
  const missingYears = useMemo(() => {
    if (!data || historicals === null) return null;
    const s = data.scenario;
    const startYear = s.start_year || new Date().getFullYear();
    const lastNeeded = startYear - 1;
    if (historicals.length === 0) return [lastNeeded];
    const years = new Set(historicals.map((h) => h.year));
    const earliest = Math.min(...historicals.map((h) => h.year));
    const missing = [];
    for (let y = earliest; y <= lastNeeded; y++) {
      if (!years.has(y)) missing.push(y);
    }
    return missing;
  }, [data, historicals]);

  useEffect(() => {
    if (missingYears === null) return; // still loading — do nothing
    if (missingYears.length > 0 && !dismissed) {
      setShowGapDialog(true);
    } else if (missingYears.length === 0) {
      // Complete history: close any earlier auto-opened dialog.
      setShowGapDialog(false);
    }
  }, [missingYears, dismissed]);

  // Sanitize a proposed filename for the OS. Replaces path separators and
  // controls with '-', trims spaces/dots and forces a max length.
  const sanitizeFilename = (name) => {
    const cleaned = (name || "").replace(/[\\/:*?"<>|\x00-\x1F]/g, "-").trim().replace(/[. ]+$/g, "");
    return cleaned.slice(0, 120) || "scenario";
  };
  const defaultBaseName = data?.scenario?.name || "scenario";
  const openExportDialog = (format) => {
    setExportDlg({ format, filename: `${sanitizeFilename(defaultBaseName)}.${format}` });
  };
  const confirmExport = async () => {
    if (!exportDlg) return;
    const { format, filename } = exportDlg;
    // Force extension consistency.
    const finalName = filename.toLowerCase().endsWith(`.${format}`)
      ? sanitizeFilename(filename.replace(/\.[^.]+$/, "")) + `.${format}`
      : `${sanitizeFilename(filename)}.${format}`;
    setExportDlg(null);
    if (format === "pdf") return withAllOpen(() => downloadPdf(finalName));
    return downloadScenarioFile(format, finalName);
  };

  const downloadScenarioFile = async (ext, customName) => {
    const token = tokenStore.get();
    try {
      const res = await fetch(`${API_BASE}/scenarios/${scenarioId}/export.${ext}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        toast.error(`Export ${res.status}`);
        return;
      }
      const blob = await res.blob();
      const name = customName || `${data.scenario.name || "scenario"}.${ext}`;
      const wrote = await saveOrDownload(blob, name, clubId);
      if (wrote) toast.success(t("Opgeslagen in map ‘{name}’").replace("{name}", name));
    } catch (e) {
      toast.error(formatApiError(e?.message));
    }
  };

  const downloadPdf = async (customName) => {
    if (!data) return;
    const s = data.scenario;
    // Landscape voor de brede transposed projectietabel.
    const doc = new jsPDF({ orientation: "landscape" });
    const pageW0 = doc.internal.pageSize.getWidth();
    const pageH0 = doc.internal.pageSize.getHeight();
    // Branding: fall back to ClubFin primary blue when the club hasn't picked a color.
    const brandRgb = hexToRgb(club?.brand_color || "#0047FF");
    // ---- Cover page ----
    // Achtergrondaccent: hoge zwarte balk bovenaan.
    doc.setFillColor(10, 10, 10);
    doc.rect(0, 0, pageW0, 70, "F");
    // Merkbadge — vervangen door club-logo indien aanwezig, anders een gekleurde streep.
    let hasLogoOnCover = false;
    if (club?.logo_data_uri) {
      try {
        doc.addImage(club.logo_data_uri, "AUTO", 20, 20, 28, 28, undefined, "FAST");
        hasLogoOnCover = true;
      } catch (err) {
        // Fall back to color bar if the image is unreadable — keep cover text left-aligned.
        console.warn("Cover logo failed to render, falling back to color bar:", err);
        doc.setFillColor(brandRgb[0], brandRgb[1], brandRgb[2]);
        doc.rect(20, 24, 10, 22, "F");
      }
    } else {
      doc.setFillColor(brandRgb[0], brandRgb[1], brandRgb[2]);
      doc.rect(20, 24, 10, 22, "F");
    }
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.text("MEERJARENPLAN ONLINE · SCENARIORAPPORT", hasLogoOnCover ? 54 : 34, 32);
    doc.setFontSize(28);
    doc.text(club?.name || "Club", hasLogoOnCover ? 54 : 34, 44);
    // Titel: scenario-naam prominent, in het witte onderdeel
    doc.setTextColor(10, 10, 10);
    doc.setFontSize(36);
    doc.text(s.name || "Scenario", 20, 110);
    // Meta-blok
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text("SCENARIO-TYPE", 20, 130);
    doc.text("HORIZON", 90, 130);
    doc.text("STARTJAAR", 140, 130);
    doc.text("RAPPORTDATUM", 190, 130);
    doc.setTextColor(10, 10, 10);
    doc.setFontSize(14);
    doc.text((s.kind || "—").toUpperCase(), 20, 138);
    doc.text(`${s.horizon_years} jaar`, 90, 138);
    doc.text(`${s.start_year}`, 140, 138);
    const today = new Date().toLocaleDateString("nl-NL", { day: "2-digit", month: "long", year: "numeric" });
    doc.text(today, 190, 138);
    // Optional user-provided summary on the cover — a bordered box under the meta strip.
    const summaryText = (s.scenario_summary || "").trim();
    if (summaryText) {
      const boxX = 20;
      const boxY = 150;
      const boxW = pageW0 - 40;
      const wrapped = doc.splitTextToSize(summaryText, boxW - 8);
      const lineH = 5.2;
      const boxH = Math.min(72, 10 + wrapped.length * lineH);
      doc.setDrawColor(10, 10, 10);
      doc.setLineWidth(0.4);
      doc.rect(boxX, boxY, boxW, boxH);
      doc.setFontSize(9);
      doc.setTextColor(120);
      doc.text("SAMENVATTING", boxX + 4, boxY + 6);
      doc.setFontSize(10);
      doc.setTextColor(10, 10, 10);
      doc.text(wrapped, boxX + 4, boxY + 12);
    }
    // Footer op cover
    doc.setFontSize(9);
    doc.setTextColor(150);
    doc.text("Vertrouwelijk — alleen voor intern bestuurlijk gebruik", 20, pageH0 - 14);

    doc.addPage();
    doc.setTextColor(10, 10, 10);
    doc.setFontSize(20);
    doc.text("MeerjarenplanOnline.nl — Scenariorapport", 14, 18);
    doc.setFontSize(12);
    doc.text(`Scenario: ${s.name}  (${(s.kind || "").toUpperCase()})`, 14, 28);
    doc.text(`Horizon: ${s.horizon_years} jaar`, 14, 34);
    doc.setFontSize(14);
    doc.text("Kernmetrieken", 14, 46);
    autoTable(doc, {
      startY: 50,
      head: [["Metriek", "Waarde"]],
      body: [
        ["Totaal nieuwe projecten", fmtInt((data.future_schedule || []).reduce((sum, r) => sum + (Number(r.amount) || 0), 0))],
        ["Basisomzet / jaar", fmtInt(data.annual_revenue_base)],
        ["Basiskosten / jaar", fmtInt(data.annual_opex_base)],
      ],
      styles: { fontSize: 10 },
    });
    // Verantwoording: gebruikte inflatie per subpost (alleen als het basisjaar subposten heeft).
    const spUsed = data.subpost_growth_used;
    if (spUsed?.items?.length) {
      const pctTxt = (r) => `${(Number(r || 0) * 100).toFixed(1).replace(".", ",")}%`;
      doc.setFontSize(14);
      doc.text("Gebruikte inflatie per subpost", 14, doc.lastAutoTable.finalY + 12);
      doc.setFontSize(9);
      doc.text(`Standaard: inkomsten ${pctTxt(spUsed.defaults?.revenue)} · bedrijfskosten ${pctTxt(spUsed.defaults?.opex)} · overige inkomsten ${pctTxt(spUsed.defaults?.other_income)} · algemene inflatie ${pctTxt(spUsed.defaults?.inflation)}`
        + (spUsed.baseline_year ? ` · basisbedragen boekjaar ${spUsed.baseline_year}` : ""), 14, doc.lastAutoTable.finalY + 18);
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 22,
        head: [["Categorie", "Subpost", "Basisbedrag", "Inflatie", "Bron"]],
        body: ["revenue", "opex", "other_income"].flatMap((cat) => spUsed.items.filter((i) => i.category === cat).map((it, idx) => [
          idx === 0 ? (CATEGORY_LABELS[cat] || cat) : "", it.name, fmtInt(it.base_amount), pctTxt(it.rate),
          it.source === "subpost" ? "eigen percentage" : `standaard ${(CATEGORY_LABELS[cat] || cat).toLowerCase()}`,
        ])),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [10, 10, 10] },
        columnStyles: { 2: { halign: "right" }, 3: { halign: "right", fontStyle: "bold" } },
      });
    }
    // Transposed projection table — matches on-screen layout (posten als rijen, jaartallen als kolommen).
    const years = data.years || [];
    const projRows = [
      ["Omzet", (y) => y.revenue],
      ["Tot. kosten - afschr.", (y) => y.total_costs_ex_dep ?? ((y.opex || 0) + (y.interest || 0) + (y.principal || 0) + (y.tax || 0))],
      ...(data.btw?.regime === "gemengd" ? [["Niet-aftrekbare BTW", (y) => y.vat_nondeductible || 0]] : []),
      [termShort("ebitda"), (y) => y.ebitda],
      ["Afschr.", (y) => y.depreciation],
      ["Rente", (y) => y.interest],
      ["Aflossing", (y) => y.principal],
      ["Belasting", (y) => y.tax],
      ["Kasstroom", (y) => y.cash_flow],
      ["Cumulatief", (y) => y.cash_position ?? y.cumulative ?? 0],
    ];
    const head = [["Post", ...years.map((y) => `${y.actual_year}\nY${y.year}`)]];
    const body = projRows.map(([label, getter]) => [label, ...years.map((y) => fmtInt(getter(y)))]);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head,
      body,
      styles: { fontSize: 7, halign: "right" },
      headStyles: { fontSize: 7, halign: "center", fillColor: [10, 10, 10] },
      columnStyles: { 0: { halign: "left", fontStyle: "bold", cellWidth: 40 } },
    });
    if (data.btw) {
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 4,
        head: [["BTW — schatting (bedragen excl. BTW)", ...years.map(() => "")]],
        body: btwScenarioRows(data.btw.regime).map(([key, label]) => [label, ...years.map((y) => (key === "pro_rata_pct" ? `${y[key] ?? 0} %` : fmtInt(y[key] || 0)))]),
        styles: { fontSize: 7, halign: "right" },
        headStyles: { fontSize: 7, halign: "left", fillColor: [90, 90, 90] },
        columnStyles: { 0: { halign: "left", fontStyle: "bold", cellWidth: 40 } },
      });
      doc.setFontSize(7); doc.setTextColor(90, 90, 90);
      doc.text(doc.splitTextToSize("Schatting op basis van één tarief per subpost (9 %/21 % niet uit totalen af te leiden). Saldo + = te betalen, − = terug te vorderen. BTW loopt via de aangifte en beïnvloedt het resultaat niet; kwartaalritme buiten beschouwing.", doc.internal.pageSize.getWidth() - 28), 14, doc.lastAutoTable.finalY + 4);
      doc.setTextColor(0, 0, 0);
    }

    // ---- Liquiditeitsopbouw (Fase B) — mirror of on-screen 15-row matrix ----
    doc.addPage();
    doc.setFontSize(14);
    doc.text("Liquiditeitsopbouw · Fase B", 14, 18);
    const liqRows = [
      ["1. Kas + bank 1/1", (y) => y.opening_cash_position ?? 0],
      ["2. Netto omzet + overige inkomsten", (y) => (y.revenue || 0) + (y.other_income || 0)],
      ["3. − Bedrijfskosten" + (data.btw?.regime === "gemengd" ? " (incl. niet-aftrekbare BTW)" : ""), (y) => -((y.opex || 0) + (y.vat_nondeductible || 0))],
      ["4. − Afschrijvingen", (y) => -(y.depreciation || 0)],
      ["5. + Rente ontvangen", (y) => y.interest_income || 0],
      ["6. − Rente bestaande leningen", (y) => -(y.interest_existing || 0)],
      ["7. − Rente nieuwe leningen", (y) => -(y.interest_new || 0)],
      ["8. = Resultaat vóór belasting", (y) => y.ebt || 0],
      ["9. − Belasting", (y) => -(y.tax || 0)],
      ["10. = Netto resultaat", (y) => y.net_income || 0],
      ["11. + Afschrijvingen (add-back)", (y) => y.depreciation || 0],
      ["12. − Aflossing nieuwe leningen", (y) => -(y.principal || 0)],
      ["13. + Nieuwe leningen ontvangen", (y) => y.loan_disbursement || 0],
      ["14. − Investeringen / reserveringen", (y) => -(y.investments_outflow || 0)],
      ["15. = Kas + bank 31/12", (y) => y.cash_position ?? 0],
      ...(data.btw ? [
        ["Memo: BTW-aangifte afdracht (−) / teruggave (+) — schatting, niet in kasstroom", (y) => -(y.vat_balance || 0)],
        ["Memo: BTW-voorfinanciering (saldo verrekend in het volgende jaar: voorgeschoten −, terug +)", (y) => y.vat_prefinancing || 0],
        ["Memo: Kas + bank 31/12 incl. BTW-voorfinanciering", (y) => y.cash_position_incl_vat ?? y.cash_position ?? 0],
      ] : []),
    ];
    const liqHead = [["Post", ...years.map((y) => `${y.actual_year}\nY${y.year}`)]];
    const liqBody = liqRows.map(([label, getter]) => [label, ...years.map((y) => fmtInt(getter(y)))]);
    autoTable(doc, {
      startY: 24,
      head: liqHead,
      body: liqBody,
      styles: { fontSize: 7, halign: "right" },
      headStyles: { fontSize: 7, halign: "center", fillColor: [10, 10, 10] },
      columnStyles: { 0: { halign: "left", fontStyle: "bold", cellWidth: 60 } },
      // Highlight opening (row 1), net result (row 10) and closing (row 15).
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 0) {
          const idx = data.row.index;
          if (idx === 0 || idx === 14) data.cell.styles.fillColor = [220, 232, 255]; // primary/10
          else if (idx === 9) data.cell.styles.fillColor = [235, 235, 235];
        }
      },
    });

    // ---- Grafieken pagina's — screenshot van elke Recharts container via html2canvas ----
    // Temporarily strip the `dark` class so the PDF gets a light-mode chart page
    // even when the user is browsing in dark mode. Restored after all captures.
    const htmlRoot = document.documentElement;
    const wasDark = htmlRoot.classList.contains("dark");
    if (wasDark) htmlRoot.classList.remove("dark");
    try {
    const descs = s.chart_descriptions || {};
    const chartTargets = [
      { testid: "cumulative-cash-flow-chart", title: t("Kasstroomtraject"), subtitle: t("Break-even grafiek · Kasstroom in de tijd"), desc: descs.cash_flow },
      { testid: "operational-profile-chart", title: t("Operationeel profiel"), subtitle: t("Omzet vs. bedrijfskosten in de tijd"), desc: descs.operational },
      { testid: "reserves-stacked-area-chart", title: t("Reserves & voorzieningen — opbouw"), subtitle: t("Cumulatieve eindsaldi per 31/12"), desc: descs.reserves_stack },
    ];
    // Landscape A4 in jsPDF (mm): 297 × 210. Margin 14mm links/rechts.
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    for (const c of chartTargets) {
      const el = document.querySelector(`[data-testid="${c.testid}"]`);
      if (!el) continue;
      try {
        const canvas = await html2canvas(el, {
          backgroundColor: "#ffffff",
          scale: 2,
          logging: false,
          ignoreElements: (node) => node.getAttribute?.("data-pdf-ignore") === "1",
        });
        const img = canvas.toDataURL("image/png");
        // Fit binnen de beschikbare content-area, met behoud van aspect ratio.
        const marginX = 14;
        const topY = 26;
        const maxW = pageW - 2 * marginX;
        // Reserve extra vertical space for the description block (if present) so
        // the chart doesn't fill the entire page and hide the caption.
        const descText = (c.desc || "").trim();
        const descWrapped = descText ? doc.splitTextToSize(descText, maxW - 4) : [];
        const descBlockH = descWrapped.length ? Math.min(50, 10 + descWrapped.length * 4.5) : 0;
        const maxH = pageH - topY - 14 - descBlockH;
        const aspect = canvas.width / canvas.height;
        let w = maxW;
        let h = w / aspect;
        if (h > maxH) { h = maxH; w = h * aspect; }
        doc.addPage();
        doc.setFontSize(9);
        doc.setTextColor(120);
        doc.text((c.subtitle || "").toUpperCase(), 14, 14);
        doc.setTextColor(0);
        doc.setFontSize(14);
        doc.text(c.title, 14, 22);
        const x = marginX + (maxW - w) / 2;
        doc.addImage(img, "PNG", x, topY, w, h, undefined, "FAST");
        if (descWrapped.length) {
          const descY = topY + h + 6;
          doc.setDrawColor(brandRgb[0], brandRgb[1], brandRgb[2]);
          doc.setLineWidth(0.8);
          doc.line(marginX, descY - 3, marginX + 2, descY - 3);
          doc.setFontSize(9);
          doc.setTextColor(120);
          doc.text("TOELICHTING", marginX, descY);
          doc.setFontSize(10);
          doc.setTextColor(10, 10, 10);
          doc.text(descWrapped, marginX, descY + 5);
        }
      } catch (err) {
        // Continue with other charts if a single one fails.
        console.warn(`Chart '${c.testid}' capture failed:`, err);
      }
    }

    // ---- Reserves projectie panel — screenshot van de tabel (indien aanwezig) + toelichting ----
    const reservesEl = document.querySelector('[data-testid="reserves-projection-panel"]');
    if (reservesEl) {
      try {
        const canvas = await html2canvas(reservesEl, {
          backgroundColor: "#ffffff",
          scale: 2,
          logging: false,
          ignoreElements: (node) => node.getAttribute?.("data-pdf-ignore") === "1",
        });
        const img = canvas.toDataURL("image/png");
        const marginX = 14;
        const topY = 26;
        const maxW = pageW - 2 * marginX;
        const descText = (descs.reserves || "").trim();
        const descWrapped = descText ? doc.splitTextToSize(descText, maxW - 4) : [];
        const descBlockH = descWrapped.length ? Math.min(50, 10 + descWrapped.length * 4.5) : 0;
        const maxH = pageH - topY - 14 - descBlockH;
        const aspect = canvas.width / canvas.height;
        let w = maxW;
        let h = w / aspect;
        if (h > maxH) { h = maxH; w = h * aspect; }
        doc.addPage();
        doc.setFontSize(9);
        doc.setTextColor(120);
        doc.text("RESERVES · PROJECTIE", 14, 14);
        doc.setTextColor(0);
        doc.setFontSize(14);
        doc.text(t("Saldi van (bestemmings)reserves"), 14, 22);
        const x = marginX + (maxW - w) / 2;
        doc.addImage(img, "PNG", x, topY, w, h, undefined, "FAST");
        if (descWrapped.length) {
          const descY = topY + h + 6;
          doc.setDrawColor(brandRgb[0], brandRgb[1], brandRgb[2]);
          doc.setLineWidth(0.8);
          doc.line(marginX, descY - 3, marginX + 2, descY - 3);
          doc.setFontSize(9);
          doc.setTextColor(120);
          doc.text("TOELICHTING", marginX, descY);
          doc.setFontSize(10);
          doc.setTextColor(10, 10, 10);
          doc.text(descWrapped, marginX, descY + 5);
        }
      } catch (err) {
        console.warn("Reserves projection capture failed:", err);
      }
    }

    // ---- Bestaande leningen · projectie panel — snapshot voor debt-overzicht ----
    const loansEl = document.querySelector('[data-testid="existing-loans-projection-panel"]');
    if (loansEl) {
      try {
        const canvas = await html2canvas(loansEl, {
          backgroundColor: "#ffffff",
          scale: 2,
          logging: false,
          ignoreElements: (node) => node.getAttribute?.("data-pdf-ignore") === "1",
        });
        const img = canvas.toDataURL("image/png");
        const marginX = 14;
        const topY = 26;
        const maxW = pageW - 2 * marginX;
        const maxH = pageH - topY - 14;
        const aspect = canvas.width / canvas.height;
        let w = maxW;
        let h = w / aspect;
        if (h > maxH) { h = maxH; w = h * aspect; }
        doc.addPage();
        // Brand-accent boven de titel — dezelfde subtiele strip als op de cover.
        doc.setFillColor(brandRgb[0], brandRgb[1], brandRgb[2]);
        doc.rect(14, 10, 10, 8, "F");
        doc.setFontSize(9);
        doc.setTextColor(120);
        doc.text("BESTAANDE LENINGEN · PROJECTIE", 28, 14);
        doc.setTextColor(0);
        doc.setFontSize(14);
        doc.text(t("Bestaande leningen — schedule"), 28, 22);
        const x = marginX + (maxW - w) / 2;
        doc.addImage(img, "PNG", x, topY, w, h, undefined, "FAST");
      } catch (err) {
        console.warn("Existing loans projection capture failed:", err);
      }
    }
    } finally {
      if (wasDark) htmlRoot.classList.add("dark");
    }

    // Apply the "PROEF" diagonal watermark to every page for TRIAL users so
    // demo exports cannot be mistaken for a real club report.
    if (user?.tier === "TRIAL") {
      applyDiagonalWatermark(doc, "PROEF");
    }

    const name = customName || `${s.name || "scenario"}.pdf`;
    const blob = doc.output("blob");
    const wrote = await saveOrDownload(blob, name, clubId);
    if (wrote) toast.success(t("Opgeslagen in map ‘{name}’").replace("{name}", name));
  };

  if (!data) {
    if (computeError) {
      return (
        <AppLayout>
          <div className="max-w-2xl border-2 border-destructive bg-destructive/5 p-6" data-testid="compute-error">
            <div className="stat-label text-destructive">{t("Fout bij berekenen")}</div>
            <h1 className="font-display font-black text-2xl uppercase tracking-tight mt-1">{t("Scenario kon niet worden berekend")}</h1>
            <p className="text-sm text-muted-foreground mt-3">{computeError}</p>
            <div className="mt-4 flex gap-2">
              <button
                onClick={loadCompute}
                data-testid="compute-error-retry"
                className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold text-sm"
              >
                {t("Opnieuw proberen")}
              </button>
              <button
                onClick={() => nav(`/clubs/${clubId}/scenarios/${scenarioId}/edit`)}
                data-testid="compute-error-edit"
                className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold text-sm"
              >
                {t("Scenario aanpassen")}
              </button>
            </div>
          </div>
        </AppLayout>
      );
    }
    return <AppLayout><div className="stat-label">{t("Berekenen…")}</div></AppLayout>;
  }
  const { scenario: s, years, npv, irr, payback_years, discounted_payback_years, capex_total, hist_context, future_context, future_schedule, reserves_projection, baseline_source, baseline_actual_year, existing_loans_projection, existing_loans_meta, has_existing_loans } = data;

  // Cash-runway warning: first projected year where cash_position drops below €0.
  const shortfallYear = (() => {
    for (const y of years || []) {
      if ((y.cash_position ?? y.cumulative ?? 0) < 0) return y;
    }
    return null;
  })();

  const kindColor = (() => {
    if (s.kind === "best") return "text-positive";
    if (s.kind === "worst") return "text-destructive";
    return "text-primary";
  })();

  // Continuous "Kasstroomtraject" line: use each historical year's actual cash+bank
  // balance (per 31/12), then continue with the projected `cash_position` from Y0 onward.
  // That way the chart shows the club's real cash position throughout — historical years
  // reflect the balance sheet, projection years extend it with each year's cash flow.
  const histRows = hist_context || [];
  // BTW-voorfinanciering (plichtig/gemengd): alleen tonen als er in een jaar echt iets wordt voorgefinancierd.
  const hasVatPrefin = !!data.btw && years.some((y) => Math.abs(Number(y.vat_prefinancing || 0)) >= 1);
  const chartData = [
    ...histRows.map((h) => {
      const depH = Number(h.depreciation || 0);
      const opex = Number(h.opex || 0);
      return {
        year: h.year,
        actual_year: h.actual_year,
        hist_revenue: h.revenue,
        hist_opex: opex,
        hist_opex_cum: opex + depH,   // stacked on top of dep_historic
        hist_balance: Number(h.cash_bank_balance || 0),
        dep_historic: depH,
        dep_total: depH,
        cumulative: Number(h.cash_bank_balance || 0),
        cumulative_incl_vat: Number(h.cash_bank_balance || 0),
      };
    }),
    ...years.map((y, i) => {
      const depHist = Number(y.dep_completed || 0);
      const depFuture = Number(y.dep_future || 0);
      const depTotal = depHist + depFuture;
      // Y0 (first projected year): duplicate onto the historical series so the dashed lines
      // (hist_revenue / hist_opex) connect visually with the historical period instead of
      // showing a visible gap between the last historical year and the first projected year.
      const isFirst = i === 0;
      // Blue line "Kaspositie 31/12": mirror of the "Cumulatief" row in the annual
      // projection table — historical years use their actual closing cash, projected years
      // use `cash_position` (= opening cash + running cumulative net cash flow through year y).
      const kaspositie = Number(y.cash_position ?? y.cumulative ?? 0);
      const opex = Number(y.opex || 0);
      return {
        ...y,
        cumulative: kaspositie,
        dcf_cumulative: kaspositie,
        cumulative_incl_vat: Number(y.cash_position_incl_vat ?? kaspositie),
        dep_historic: depHist,
        dep_total: depTotal,
        opex_cum: opex + depTotal,  // stacked on top of dep_total
        ...(isFirst && histRows.length > 0
          ? { hist_revenue: Number(y.revenue || 0), hist_opex: opex, hist_opex_cum: opex + depTotal }
          : {}),
      };
    }),
  ];

  return (
    <AppLayout>
      <Dialog open={showGapDialog} onOpenChange={(open) => { setShowGapDialog(open); if (!open) setDismissed(true); }}>
        <DialogContent data-testid="historical-gap-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-secondary" /> {t("Historische data ontbreekt")}
            </DialogTitle>
            <DialogDescription>
              {t("Voor een correcte projectie zijn cijfers nodig voor elk jaar tot en met {last}, met daarin ook het saldo van kas en bank per 31/12.").replace("{last}", String((s.start_year || new Date().getFullYear()) - 1))}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <div className="stat-label mb-2">{t("Ontbrekende jaren:")}</div>
            <div className="flex flex-wrap gap-2" data-testid="missing-years">
              {(missingYears || []).map((y) => (
                <span key={y} className="px-2 py-1 border-2 border-destructive bg-destructive/10 text-destructive font-mono-num font-bold text-sm">{y}</span>
              ))}
            </div>
          </div>
          <DialogFooter className="mt-4 gap-2">
            <button
              onClick={() => { setShowGapDialog(false); setDismissed(true); }}
              data-testid="gap-dismiss"
              className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold text-sm"
            >{t("Later")}</button>
            <button
              onClick={() => nav(`/clubs/${clubId}`)}
              data-testid="gap-goto-historicals"
              className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold text-sm"
            >{t("Ga naar Voorgaande jaren")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDupDialog} onOpenChange={(open) => { if (!dupSaving) setShowDupDialog(open); }}>
        <DialogContent data-testid="duplicate-scenario-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl flex items-center gap-2">
              <Copy className="w-5 h-5" /> {t("Scenario kopiëren")}
            </DialogTitle>
            <DialogDescription>
              {t("Geef de kopie een naam. Alle instellingen, investeringen en groeivoeten worden overgenomen.")}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <label className="stat-label">{t("Nieuwe scenarionaam")}</label>
            <input
              type="text"
              value={dupName}
              onChange={(e) => setDupName(e.target.value)}
              data-testid="duplicate-scenario-name-input"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); document.querySelector('[data-testid="duplicate-scenario-confirm"]')?.click(); } }}
              className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <DialogFooter className="mt-4 gap-2">
            <button
              onClick={() => setShowDupDialog(false)}
              disabled={dupSaving}
              data-testid="duplicate-scenario-cancel"
              className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold">
              {t("Annuleren")}
            </button>
            <button
              onClick={async () => {
                const name = (dupName || "").trim() || `${s.name} (kopie)`;
                setDupSaving(true);
                try {
                  const { data: created } = await api.post(`/scenarios/${scenarioId}/duplicate`, { name });
                  toast.success(t("Scenario gekopieerd"));
                  setShowDupDialog(false);
                  nav(`/clubs/${clubId}/scenarios/${created.id}/edit`);
                } catch (err) {
                  toast.error(formatApiError(err.response?.data?.detail));
                } finally {
                  setDupSaving(false);
                }
              }}
              disabled={dupSaving}
              data-testid="duplicate-scenario-confirm"
              className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold">
              {dupSaving ? t("Bezig…") : t("Kopiëren")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(exportDlg)} onOpenChange={(open) => { if (!open) setExportDlg(null); }}>
        <DialogContent data-testid="export-filename-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl flex items-center gap-2">
              <Download className="w-5 h-5" /> {t("Bestand opslaan")}
            </DialogTitle>
            <DialogDescription>
              {t("Pas de bestandsnaam aan of gebruik de suggestie. De extensie {ext} wordt automatisch toegepast.").replace("{ext}", `.${exportDlg?.format || ""}`)}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <label className="stat-label">{t("Bestandsnaam")}</label>
            <input
              type="text"
              value={exportDlg?.filename || ""}
              onChange={(e) => setExportDlg((d) => d ? { ...d, filename: e.target.value } : d)}
              data-testid="export-filename-input"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirmExport(); } }}
              className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 font-mono-num focus:outline-none focus:ring-2 focus:ring-primary"
            />
            {folderName && (
              <p className="text-xs text-muted-foreground mt-2">
                {t("Opslaan in map ‘{name}’").replace("{name}", folderName)}
              </p>
            )}
          </div>
          <DialogFooter className="mt-4 gap-2">
            <button
              onClick={() => setExportDlg(null)}
              data-testid="export-filename-cancel"
              className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold">
              {t("Annuleren")}
            </button>
            <button
              onClick={confirmExport}
              data-testid="export-filename-confirm"
              className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold">
              {t("Downloaden")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <button onClick={() => nav(`/clubs/${clubId}`)} data-testid="back-club"
        className="text-sm inline-flex items-center gap-1 text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-4 h-4" /> {t("Terug naar club")}
      </button>

      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className={`stat-label ${kindColor}`}>{s.kind?.toUpperCase()} · {s.horizon_years}{t("-jarige horizon")}</div>
          <h1 className="font-display font-black text-5xl md:text-6xl uppercase tracking-tighter mt-1">{s.name}</h1>
          {s.notes && <p className="text-muted-foreground mt-2 max-w-2xl">{s.notes}</p>}
          {baseline_source === "last_historical" && (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="active-overlays">
              <OverlayBadge icon={<History className="w-3 h-3" />} label={`${t("Basis: laatste boekjaar")} ${baseline_actual_year ?? ""}`} />
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap" data-testid="scenario-actions">
          <TourButton onStart={startTour} disabled={tourDisabled} />
          <Link to={`/clubs/${clubId}/scenarios/${scenarioId}/edit`} data-testid="edit-btn"
            className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold">{t("Bewerken")}</Link>
          <button
            onClick={() => { setDupName(`${s.name} (kopie)`); setShowDupDialog(true); }}
            data-testid="duplicate-btn"
            className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold inline-flex items-center gap-2">
            <Copy className="w-4 h-4" /> {t("Dupliceer")}
          </button>
          <button onClick={() => setDescDlgOpen(true)} data-testid="chart-descriptions-open"
            title={t("Toelichtingen bij de grafieken (verschijnen ook in de PDF)")}
            className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold inline-flex items-center gap-2">
            <MessageSquare className="w-4 h-4" /> {t("Toelichtingen")}
          </button>
          <button onClick={() => openExportDialog("csv")} data-testid="download-csv"
            className="btn-brutal bg-secondary text-secondary-foreground px-4 py-2 font-display uppercase font-bold inline-flex items-center gap-2">
            <Download className="w-4 h-4" /> CSV
          </button>
          <button onClick={() => openExportDialog("xlsx")} data-testid="download-xlsx"
            className="btn-brutal bg-secondary text-secondary-foreground px-4 py-2 font-display uppercase font-bold inline-flex items-center gap-2">
            <Download className="w-4 h-4" /> Excel
          </button>
          <button onClick={() => openExportDialog("pdf")} data-testid="download-pdf"
            className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold inline-flex items-center gap-2">
            <FileText className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

      <ClubTour steps={tourSteps} open={tourOpen} onClose={() => { setTourOpen(false); setForceOpen(false); }} onDisable={() => { setTourDisabled(true); setTourDisabledState(true); }} />
      <ChartDescriptionsDialog open={descDlgOpen} onOpenChange={setDescDlgOpen} values={s.chart_descriptions || {}}
        onSave={(chartKey, description) => saveScenarioDescription({ chartKey, description })} />
      <div ref={reportRef}>

        {/* Scenario-samenvatting — verschijnt op de cover van de PDF-export */}
        <div className="mb-6" data-testid="scenario-summary-block">
          <ChartDescriptionEditor
            value={s.scenario_summary || ""}
            onSave={(txt) => saveScenarioDescription({ summary: txt })}
            label={t("Samenvatting toevoegen")}
            editLabel={t("Bewerk samenvatting")}
            dialogTitle={t("Scenario-samenvatting")}
            testid="scenario-summary"
            maxLength={800}
            onGenerate={hasAiAccess ? generateAiSummary : undefined}
            generateLabel={t("AI genereer")}
          />
        </div>

        {shortfallYear && (
          <div
            data-testid="cash-runway-warning"
            className="mb-6 border-2 border-destructive bg-destructive/10 p-4 heavy-shadow flex items-start gap-3"
          >
            <AlertTriangle className="w-6 h-6 text-destructive shrink-0 mt-0.5" />
            <div>
              <div className="font-display font-black uppercase tracking-tight text-destructive text-lg">
                {term("cashShortfall")} · {shortfallYear.actual_year}
              </div>
              <div className="text-sm text-foreground mt-1 max-w-3xl">
                {t("De verwachte cashpositie zakt onder €0 in {year}. Overweeg extra financiering of het investeringsschema aan te passen.").replace("{year}", String(shortfallYear.actual_year))}
              </div>
              <div className="text-xs font-mono-num mt-2 text-muted-foreground">
                Y{shortfallYear.year} · {t("Cumulatief")}: <span className="text-destructive font-bold">{fmtInt(shortfallYear.cash_position ?? shortfallYear.cumulative)}</span>
              </div>
            </div>
          </div>
        )}

        <ReportNav sections={REPORT_SECTIONS.map((x) => ({ ...x, hidden: (x.id === "plans" && !(future_schedule || []).length) || (x.id === "inflation" && !(data.subpost_growth_used?.items || []).length) || ((x.id === "reserves" || x.id === "reserves_chart") && !(reserves_projection && (reserves_projection.reserves || []).length)) || (x.id === "loans" && !(has_existing_loans && (existing_loans_projection || []).length)) }))} openMap={openMap} onJump={jumpToSection} onAll={setAllSections} />

        <ReportSection id="plans" group="overzicht" title={REPORT_SECTIONS.find((x) => x.id === "plans").title} open={openMap.plans} forceOpen={forceOpen} onToggle={toggleSection} hidden={!(future_schedule || []).length}>
          <FutureScheduleTable t={t} schedule={future_schedule} years={years} />
        </ReportSection>
        <ReportSection id="glance" group="overzicht" title={REPORT_SECTIONS.find((x) => x.id === "glance").title} open={openMap.glance} forceOpen={forceOpen} onToggle={toggleSection} hidden={false}>
        <div className="space-y-6">
          <div className="bg-card border-2 border-foreground p-6 heavy-shadow" data-testid="at-a-glance-card">
            <div className="stat-label">{t("Structuur")}</div>
            <h2 className="font-display font-black text-2xl uppercase tracking-tight mb-4">{t("In één oogopslag")}</h2>
            <ul className="grid md:grid-cols-2 gap-x-12 gap-y-3">
              <StructureRow k={t("Totaal nieuwe projecten")} v={fmtCurrency((future_schedule || []).reduce((sum, r) => sum + (Number(r.amount) || 0), 0))} />
              <StructureRow k={t("Lening")} v={fmtCurrency((Number(s.loan_amount) || 0) + (s.scenario_future_investments || []).reduce((sum, it) => sum + (Number(it.loan_amount) || 0), 0))} testid="structure-loan" />
              <StructureRow k={t("Eigen inbreng")} v={fmtCurrency((s.scenario_future_investments || []).reduce((sum, it) => sum + Math.max(0, (Number(it.amount) || 0) - (Number(it.loan_amount) || 0)), 0))} />
              <StructureRow k={t("Basisomzet / jaar")} v={fmtCurrency(data.annual_revenue_base)} />
              <StructureRow k={t("Basiskosten / jaar")} v={fmtCurrency(data.annual_opex_base)} />
              <StructureRow k={t("Belasting")} v={fmtPct(s.tax_rate)} />
              <StructureRow k={t("Inflatie")} v={fmtPct(s.inflation_rate)} />
            </ul>
          </div>

          <div className="bg-card border-2 border-foreground p-6 heavy-shadow" data-testid="cumulative-cash-flow-chart">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="stat-label">{t("Break-even grafiek · Kasstroom in de tijd")}</div>
                <h2 className="font-display font-black text-2xl uppercase tracking-tight mb-4">{t("Kasstroomtraject")}</h2>
              </div>
              <HelpButton section="kasstroomtraject" label="Uitleg kasstroom" size="sm" />
            </div>
            <div className="h-[360px] lg:h-[480px]">
              <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 500, height: 480 }}>
                <ComposedChart data={chartData}>
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="0" />
                  <XAxis dataKey="actual_year" />
                  <YAxis tickFormatter={(v) => fmtCurrency(v)} width={70} />
                  <Tooltip formatter={(v) => fmtCurrency(v)} />
                  <Legend verticalAlign="bottom" content={LegendWithHint({ hint: t("Positie per 31/12") })} />
                  <ReferenceLine y={0} stroke="#0A0A0A" strokeWidth={2} />
                  <ReferenceLine x={s.start_year || new Date().getFullYear()} stroke="#94a3b8" strokeDasharray="3 3" />
                  <Bar dataKey="hist_balance" name={t("Historische kaspositie 31/12")} fill="#94a3b8" />
                  <Bar dataKey="loan_disbursement" name={t("Lening ontvangen")} fill="#00D05A" />
                  <Bar dataKey="cash_flow" name={t("Kasstroom")} fill="#FF5900" />
                  {hasVatPrefin && <Bar dataKey="vat_prefinancing" name={t("BTW-voorfinanciering")} fill="#7c3aed" />}
                  <Line type="monotone" dataKey="cumulative" name={t("Kaspositie 31/12")} stroke="#0047FF" strokeWidth={3} dot={false} />
                  {hasVatPrefin && <Line type="monotone" dataKey="cumulative_incl_vat" name={t("Kaspositie 31/12 incl. BTW-voorfinanciering")} stroke="#7c3aed" strokeWidth={2} strokeDasharray="6 4" dot={false} />}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            {hasVatPrefin && (
              <p className="mt-2 text-xs text-muted-foreground" data-testid="cash-flow-vat-prefin-note">
                {t("Paarse staaf en stippellijn: BTW-voorfinanciering — het deel van het BTW-saldo dat pas in het volgende jaar met de Belastingdienst wordt verrekend ({pct} % bij {tijdvak}aangifte; aan te passen op de BTW-tab van de club). Zo zie je hoe diep de bankstand tijdelijk zakt als je de BTW op een investering voorschiet. Schatting; alle overige bedragen zijn excl. BTW.")
                  .replace("{pct}", String(data.btw.prefin_pct ?? 25)).replace("{tijdvak}", data.btw.aangiftetijdvak === "jaar" ? t("jaar") : t("kwartaal"))}
              </p>
            )}
            <ChartDescriptionEditor readOnly value={(s.chart_descriptions || {}).cash_flow || ""} testid="chart-desc-cash_flow" />
          </div>
        </div>

        </ReportSection>
        <ReportSection id="operational" group="overzicht" title={REPORT_SECTIONS.find((x) => x.id === "operational").title} open={openMap.operational} forceOpen={forceOpen} onToggle={toggleSection} hidden={false}>
        <div className="bg-card border-2 border-foreground p-6 heavy-shadow" data-testid="operational-profile-chart">
          <div className="stat-label">{t("Omzet vs. bedrijfskosten in de tijd")}</div>
          <h2 className="font-display font-black text-2xl uppercase tracking-tight mb-4">{t("Operationeel profiel")}</h2>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 500, height: 280 }}>
              <LineChart data={chartData}>
                <CartesianGrid stroke="#e2e8f0" />
                <XAxis dataKey="actual_year" />
                <YAxis tickFormatter={(v) => fmtCurrency(v)} width={70} />
                <Tooltip formatter={(v) => fmtCurrency(v)} />
                <Legend verticalAlign="bottom" content={LegendWithHint({ hint: t("Positie per 31/12") })} />
                <ReferenceLine x={s.start_year || new Date().getFullYear()} stroke="#94a3b8" strokeDasharray="3 3" />
                <Line type="monotone" dataKey="hist_revenue" stroke="#94a3b8" strokeWidth={2} dot={false} name={t("Historische omzet")} strokeDasharray="4 4" />
                <Line type="monotone" dataKey="hist_opex_cum" stroke="#f97316" strokeWidth={2} dot={false} name={t("Historische bedr. kosten + afschr.")} strokeDasharray="4 4" />
                <Line type="monotone" dataKey="revenue" stroke="#0047FF" strokeWidth={3} dot={false} name={t("Omzet")} />
                <Line type="monotone" dataKey="opex_cum" stroke="#FF5900" strokeWidth={3} dot={false} name={t("Bedr. kosten + afschr.")} />
                <Line type="monotone" dataKey="dep_historic" stroke="#7c3aed" strokeWidth={2} dot={false} name={t("Afschr. historisch")} strokeDasharray="4 2" />
                <Line type="monotone" dataKey="dep_total" stroke="#00D05A" strokeWidth={2} dot={false} name={t("Afschr. historisch + nieuwe inv.")} strokeDasharray="4 2" />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <ChartDescriptionEditor readOnly value={(s.chart_descriptions || {}).operational || ""} testid="chart-desc-operational" />
        </div>

        </ReportSection>
        <ReportSection id="inflation" group="detail" title={REPORT_SECTIONS.find((x) => x.id === "inflation").title} open={openMap.inflation} forceOpen={forceOpen} onToggle={toggleSection} hidden={!(data.subpost_growth_used?.items || []).length}>
          <SubpostGrowthTable t={t} used={data.subpost_growth_used} />
        </ReportSection>
        <ReportSection id="liquidity" group="detail" title={REPORT_SECTIONS.find((x) => x.id === "liquidity").title} open={openMap.liquidity} forceOpen={forceOpen} onToggle={toggleSection} hidden={false}>
          <LiquidityBuildupTable t={t} years={years} btw={data.btw} />
          <EbitdaStrip t={t} years={years} />
          {data.btw && <BtwStrip t={t} years={years} regime={data.btw.regime} />}
        </ReportSection>
        <ReportSection id="reserves_chart" group="detail" title={REPORT_SECTIONS.find((x) => x.id === "reserves_chart").title} open={openMap.reserves_chart} forceOpen={forceOpen} onToggle={toggleSection} hidden={!(reserves_projection && (reserves_projection.reserves || []).length)}>
        <ReservesStackedAreaChart
          t={t}
          reservesProjection={reserves_projection}
          startYear={s.start_year || new Date().getFullYear()}
          descriptionValue={(s.chart_descriptions || {}).reserves_stack || ""}
          onSaveDescription={(txt) => saveScenarioDescription({ chartKey: "reserves_stack", description: txt })}
        />

        </ReportSection>
        <ReportSection id="reserves" group="detail" title={REPORT_SECTIONS.find((x) => x.id === "reserves").title} open={openMap.reserves} forceOpen={forceOpen} onToggle={toggleSection} hidden={!(reserves_projection && (reserves_projection.reserves || []).length)}>
        <ReservesProjectionPanel
          t={t}
          projection={reserves_projection}
          descriptionValue={(s.chart_descriptions || {}).reserves || ""}
          onSaveDescription={(txt) => saveScenarioDescription({ chartKey: "reserves", description: txt })}
        />

        </ReportSection>
        <ReportSection id="loans" group="detail" title={REPORT_SECTIONS.find((x) => x.id === "loans").title} open={openMap.loans} forceOpen={forceOpen} onToggle={toggleSection} hidden={!(has_existing_loans && (existing_loans_projection || []).length)}>
        <ExistingLoansProjectionPanel
          t={t}
          projection={existing_loans_projection}
          meta={existing_loans_meta}
          hasLoans={has_existing_loans}
        />

        </ReportSection>
        <ReportSection id="sensitivity" group="risico" title={REPORT_SECTIONS.find((x) => x.id === "sensitivity").title} open={openMap.sensitivity} forceOpen={forceOpen} onToggle={toggleSection} hidden={false}>
          <SensitivityPanel sid={scenarioId} scenario={s} />
        </ReportSection>
        <ReportSection id="montecarlo" group="risico" title={REPORT_SECTIONS.find((x) => x.id === "montecarlo").title} open={openMap.montecarlo} forceOpen={forceOpen} onToggle={toggleSection} hidden={false}>
          <MonteCarloPanel t={t} scenario={s} sid={scenarioId} clubId={clubId} />
        </ReportSection>
      </div>
    </AppLayout>
  );
}

// Return a Tailwind class name for a signed numeric value: positive/negative/zero.
const signedClass = (v) => {
  if (v > 0) return "text-positive";
  if (v < 0) return "text-destructive";
  return "";
};

// Small caption + default legend items, rendered as the Recharts Legend content prop.
// The caption sits above the legend so it reads like "Positie per 31/12" boven de kleuren.
function LegendWithHint({ hint }) {
  return function CustomLegend(props) {
    const { payload = [] } = props;
    return (
      <div className="text-center -mt-2 text-xs">
        <div className="italic text-muted-foreground mb-0.5" data-testid="chart-hint">{hint}</div>
        <div className="flex justify-center gap-3 flex-wrap">
          {payload.map((entry, i) => (
            <span key={`legend-${i}`} className="inline-flex items-center gap-1">
              <span
                className="inline-block w-3 h-3 border border-foreground/20"
                style={{ backgroundColor: entry.color || entry.payload?.stroke || entry.payload?.fill }}
              />
              <span>{entry.value}</span>
            </span>
          ))}
        </div>
      </div>
    );
  };
}

// ---- Liquiditeitsopbouw (Fase B) ----
// Rijen berekend uit years-array; opex = bedrijfskosten excl. afschr; ebit = ebitda - dep.
const LIQUIDITY_ROWS = [
  { key: "opening_cash_position", label: "1. Kas + bank 1/1", opening: true },
  { key: "revenue_plus_oi", label: "2. Netto omzet + overige inkomsten", val: (y) => (y.revenue || 0) + (y.other_income || 0) },
  { key: "opex_out", label: "3. − Bedrijfskosten", val: (y) => -((y.opex || 0) + (y.vat_nondeductible || 0)), negative: true },
  { key: "depreciation_out", label: "4. − Afschrijvingen", val: (y) => -(y.depreciation || 0), negative: true },
  { key: "interest_income", label: "5. + Rente ontvangen", val: (y) => y.interest_income || 0, positive: true },
  { key: "interest_existing_out", label: "6. − Rente bestaande leningen", val: (y) => -(y.interest_existing || 0), negative: true },
  { key: "interest_new_out", label: "7. − Rente nieuwe leningen", val: (y) => -(y.interest_new || 0), negative: true },
  { key: "ebt", label: "8. = Resultaat vóór belasting" },
  { key: "tax_out", label: "9. − Belasting", val: (y) => -(y.tax || 0), negative: true },
  { key: "net_income", label: "10. = Netto resultaat", subtotal: true },
  { key: "depreciation_add", label: "11. + Afschrijvingen (add-back)", val: (y) => y.depreciation || 0, positive: true },
  { key: "principal_out", label: "12. − Aflossing nieuwe leningen", val: (y) => -(y.principal || 0), negative: true },
  { key: "loan_disbursement", label: "13. + Nieuwe leningen ontvangen", val: (y) => y.loan_disbursement || 0, positive: true },
  { key: "investments_outflow_out", label: "14. − Investeringen / reserveringen", val: (y) => -(y.investments_outflow || 0), negative: true },
  // Kasstroom tussen 14 en 15 — lichtblauwe achtergrond + zwarte top-border,
  // GEEN border-bottom (grens met "= Kas + bank 31/12" blijft naadloos).
  { key: "cash_flow", label: "14b. = Kasstroom", val: (y) => y.cash_flow || 0, cashflow: true, signed: true },
  { key: "cash_position", label: "15. = Kas + bank 31/12", closing: true },
];

// Memo-regels BTW (plichtig/gemengd): aangiftesaldo (schatting) en voorfinanciering: het deel van het saldo dat pas in het
// volgende jaar wordt verrekend (aangiftetijdvak club). Niet in de kasstroom omdat alle bedragen excl. BTW zijn.
const LIQ_BTW_MEMO_ROWS = [
  { key: "btw_memo", label: "Memo: BTW-aangifte afdracht (−) / teruggave (+) — schatting, zie BTW-blok; niet in kasstroom", val: (y) => -(y.vat_balance || 0), memo: true },
  { key: "btw_prefin", label: "Memo: BTW-voorfinanciering (saldo verrekend in het volgende jaar: voorgeschoten −, terug +)", val: (y) => y.vat_prefinancing || 0, memo: true, signed: true },
  { key: "btw_cash_incl", label: "Memo: Kas + bank 31/12 incl. BTW-voorfinanciering", val: (y) => y.cash_position_incl_vat ?? y.cash_position ?? 0, memo: true, memoClosing: true },
];

// EBITDA-strip — apart klein vak met alleen EBITDA per jaar, tussen
// Liquiditeitsopbouw en Bestemmingsreserves. Geeft bestuur direct zicht op
// operationeel resultaat vóór afschrijving/rente/belasting.
function EbitdaStrip({ t, years }) {
  if (!years || years.length === 0) return null;
  return (
    <div className="bg-card border-2 border-foreground heavy-shadow mt-6 mb-10 overflow-x-auto" data-testid="ebitda-strip">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-foreground text-background">
            <th className="text-left px-3 py-2 font-display uppercase text-xs tracking-widest sticky left-0 bg-foreground w-64">
              {termShort("ebitda")}
              <span className="normal-case tracking-normal font-normal opacity-70 text-[10px] ml-2">
                {t("Winst vóór rente, belasting & afschrijving")}
              </span>
            </th>
            {years.map((y) => (
              <th
                key={y.year}
                className="text-right px-3 py-2 font-display uppercase text-xs tracking-widest whitespace-nowrap"
                data-testid={`ebitda-col-${y.year}`}
              >
                {y.actual_year}
                <span className="opacity-70 text-[10px] ml-1">· Y{y.year}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="px-3 py-2.5 sticky left-0 bg-card font-semibold">{termShort("ebitda")}</td>
            {years.map((y) => {
              const v = y.ebitda || 0;
              return (
                <td
                  key={y.year}
                  data-testid={`ebitda-cell-${y.year}`}
                  className={`px-3 py-2.5 font-mono-num text-right whitespace-nowrap font-bold ${v >= 0 ? "text-positive" : "text-destructive"}`}
                >
                  {fmtInt(v)}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}


function LiquidityBuildupTable({ t, years, btw }) {
  if (!years || years.length === 0) return null;
  const rows = btw ? [...LIQUIDITY_ROWS, ...LIQ_BTW_MEMO_ROWS] : LIQUIDITY_ROWS;
  return (
    <div className="bg-card border-2 border-foreground heavy-shadow mt-8 overflow-x-auto" data-testid="liquidity-buildup">
      <div className="p-5 border-b-2 border-foreground flex items-start justify-between gap-3">
        <div>
          <div className="stat-label">{t("Fase B · Kasstroom-detail")}</div>
          <h2 className="font-display font-black text-2xl uppercase tracking-tight">{t("Liquiditeitsopbouw")}</h2>
        </div>
        <HelpButton section="liquiditeitsopbouw" label="Uitleg liquiditeitsopbouw" size="sm" />
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-foreground text-background">
            <th className="text-left px-3 py-2 font-display uppercase text-xs tracking-widest sticky left-0 bg-foreground">{t("Post")}</th>
            {years.map((y) => {
              const cash = y.cash_position ?? 0;
              const shortfall = cash < 0;
              return (
                <th
                  key={y.year}
                  className={`text-right px-3 py-2 font-display uppercase text-xs tracking-widest whitespace-nowrap ${shortfall ? "bg-destructive text-background" : ""}`}
                >
                  {y.actual_year}
                  <span className="opacity-70 text-[10px] ml-1">· Y{y.year}</span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const emphasis = row.opening
              ? "bg-primary/10 font-black border-t-2 border-foreground"
              : row.closing
              // Closing heeft GEEN border-t: de zwarte lijn zit al bovenaan de Kasstroom-rij.
              ? "bg-primary/10 font-black"
              : row.cashflow
              // Kasstroom (rij 14b): lichtblauw + zwarte lijn erboven. Geen lijn eronder.
              ? "bg-sky-100 dark:bg-sky-900/40 font-bold border-t-2 border-foreground"
              : row.subtotal
              ? "bg-muted/60 font-bold"
              : row.memo
              ? "italic text-muted-foreground border-t-2 border-dashed border-foreground/40"
              : "even:bg-muted/40";
            return (
              <tr key={row.key} className={emphasis} data-testid={`liq-row-${row.key}`}>
                <td className="px-3 py-2 sticky left-0 bg-inherit whitespace-nowrap">{t(row.label)}</td>
                {years.map((y) => {
                  const raw = row.val ? row.val(y) : (y[row.key] ?? 0);
                  const shortfall = (y.cash_position ?? 0) < 0;
                  // Kleur: closing/subtotal/cashflow → gesigneerd; positive/negative → vast; rest neutraal.
                  let colorCls = "";
                  if (row.closing || row.memoClosing) colorCls = raw >= 0 ? "text-positive" : "text-destructive";
                  else if (row.cashflow || row.subtotal || row.signed) colorCls = signedClass(raw);
                  else if (row.positive) colorCls = "text-positive";
                  else if (row.negative) colorCls = "text-destructive";
                  const bg = shortfall && !row.opening && !row.closing && !row.cashflow && !row.memo ? "bg-destructive/10" : "";
                  return (
                    <td
                      key={y.year}
                      data-testid={`liq-cell-${row.key}-${y.year}`}
                      className={`px-3 py-2 font-mono-num text-right whitespace-nowrap ${colorCls} ${bg}`}
                    >
                      {fmtInt(raw)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {btw && (
        <p className="px-5 py-3 text-xs text-muted-foreground border-t border-foreground/20" data-testid="liq-btw-memo-note">
          {t("De memo-regels tonen het geschatte saldo van de BTW-aangifte (uit het BTW-blok hieronder) en de BTW-voorfinanciering: het deel van dat saldo dat pas in het volgende jaar met de Belastingdienst wordt verrekend ({pct} % bij {tijdvak}aangifte — instelbaar op de BTW-tab van de club). Alle bedragen in deze opbouw zijn excl. BTW: de aangifte loopt via de Belastingdienst en zit daarom niet in de kasstroom; de regel 'Kas + bank 31/12 incl. BTW-voorfinanciering' laat zien hoe diep de bankstand tijdelijk zakt als je de BTW op een investering voorschiet.")
            .replace("{pct}", String(btw.prefin_pct ?? 25)).replace("{tijdvak}", btw.aangiftetijdvak === "jaar" ? t("jaar") : t("kwartaal"))}
          {btw.regime === "gemengd" && " " + t("Het niet-aftrekbare deel van de BTW zit wél in de kasstroom: bij de bedrijfskosten (rij 3) en in het investeringsbedrag (rij 14).")}
        </p>
      )}
    </div>
  );
}

function MetricCard({ label, value, accent, testid }) {  const color = {
    positive: "bg-positive text-white",
    destructive: "bg-destructive text-white",
    primary: "bg-primary text-primary-foreground",
    secondary: "bg-secondary text-secondary-foreground",
    accent: "bg-accent text-accent-foreground",
  }[accent] || "bg-primary text-primary-foreground";
  return (
    <div className={`border-2 border-foreground heavy-shadow ${color} p-5`} data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] opacity-80">{label}</div>
      <div className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-2 leading-none">{value}</div>
    </div>
  );
}

function StructureRow({ k, v, testid }) {
  return (
    <li className="flex items-center justify-between border-b border-border pb-2" data-testid={testid}>
      <span className="stat-label">{k}</span>
      <span className="font-mono-num font-bold">{v}</span>
    </li>
  );
}

function OverlayBadge({ icon, label }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 border-2 border-primary bg-primary/10 text-primary font-display uppercase text-xs tracking-widest font-bold">
      {icon}{label}
    </span>
  );
}

function FutureScheduleTable({ t, schedule, years }) {
  if (!schedule || schedule.length === 0) return null;
  // Union of all projected years from either the projection or the schedule keys.
  const yearSet = new Set();
  (years || []).forEach((y) => { if (y.year > 0) yearSet.add(y.actual_year); });
  schedule.forEach((row) => Object.keys(row.yearly || {}).forEach((y) => yearSet.add(Number(y))));
  const cols = [...yearSet].sort((a, b) => a - b);
  const fmt = fmtInt;
  return (
    <div className="bg-card border-2 border-foreground heavy-shadow mb-8 overflow-x-auto" data-testid="future-schedule">
      <div className="p-5 border-b-2 border-foreground">
        <div className="stat-label">{t("Investeringsplan")}</div>
        <h2 className="font-display font-black text-2xl uppercase tracking-tight">{t("Investeringsplannen")}</h2>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-foreground text-background">
            <th className="text-left px-3 py-2 font-display uppercase text-xs tracking-widest sticky left-0 bg-foreground">{t("Investering")}</th>
            <th className="text-right px-3 py-2 font-display uppercase text-xs tracking-widest">{t("Bedrag")}</th>
            <th className="text-right px-3 py-2 font-display uppercase text-xs tracking-widest">{t("Startjaar")}</th>
            <th className="text-right px-3 py-2 font-display uppercase text-xs tracking-widest">{t("Looptijd")}</th>
            <th className="text-right px-3 py-2 font-display uppercase text-xs tracking-widest">{t("Bron")}</th>
            {cols.map((y) => (
              <th key={y} className="text-right px-3 py-2 font-display uppercase text-xs tracking-widest">{y}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {schedule.map((row, idx) => (
            <tr key={`${row.name}-${row.start_year}-${idx}`} className={idx % 2 ? "bg-muted/40" : ""} data-testid={`future-schedule-row-${idx}`}>
              <td className="px-3 py-2 sticky left-0 bg-inherit whitespace-nowrap font-semibold">{row.name}</td>
              <td className="px-3 py-2 font-mono-num text-right">
                {fmt(row.amount)}
                {row.inflation_years > 0 && row.amount_original && (
                  <div className="text-[10px] text-muted-foreground" title={t("Prijspeil {y} + {n}j × {r}%").replace("{y}", row.money_year).replace("{n}", row.inflation_years).replace("{r}", (row.cost_inflation_rate * 100).toFixed(1))}>
                    ({fmt(row.amount_original)} @ {row.money_year})
                  </div>
                )}
              </td>
              <td className="px-3 py-2 font-mono-num text-right">{row.start_year}</td>
              <td className="px-3 py-2 font-mono-num text-right">{row.depreciation_years > 0 ? `${row.depreciation_years} ${t("jr")}` : "—"}</td>
              <td className="px-3 py-2 text-right">
                <span className={`px-2 py-1 border-2 text-[10px] font-bold uppercase tracking-widest ${row.funded_from_reservation ? "border-secondary bg-secondary/10 text-secondary" : "border-primary bg-primary/10 text-primary"}`}>
                  {row.funded_from_reservation ? t("Reservering") : t("Lening/eigen")}
                </span>
              </td>
              {cols.map((y) => (
                <td key={y} className="px-3 py-2 font-mono-num text-right">
                  {row.yearly?.[y] ? fmt(row.yearly[y]) : "—"}
                </td>
              ))}
            </tr>
          ))}
          <tr className="border-t-2 border-foreground bg-primary/10 font-black">
            <td className="px-3 py-2 sticky left-0 bg-primary/10 font-display uppercase tracking-wider text-sm">{t("Totaal per jaar")}</td>
            <td className="px-3 py-2" colSpan={4}></td>
            {cols.map((y) => {
              const total = schedule.reduce((sum, r) => sum + (Number(r.yearly?.[y]) || 0), 0);
              return (
                <td key={y} className="px-3 py-2 font-mono-num text-right" data-testid={`future-schedule-total-${y}`}>
                  {total > 0 ? fmt(total) : "—"}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ---- Reserves & voorzieningen — cumulatieve stacked-area grafiek ----
// Toont per boekjaar de eindsaldi (per 31/12) van drie groepen:
//   1) Algemene reserve (category="general")
//   2) Bestemmingsreserves (category="earmarked" zonder "voorziening" in de naam)
//   3) Voorzieningen (category="earmarked" MET "voorziening" in de naam)
// De totale hoogte per jaar = totale reserve-positie van de club. Stacked-area
// geeft direct visueel dat de bijdragen optellen — dat is wat de gebruiker met
// "cumulatief" bedoelt (net als de opgetelde bedr. kosten + afschr. in het
// operationeel profiel). Voorzieningen en bestemmingsreserves worden apart
// weergegeven zodat toezicht en accountant hun karakter snel herkennen.
function ReservesStackedAreaChart({ t, reservesProjection, startYear, descriptionValue, onSaveDescription }) {
  const proj = reservesProjection || {};
  const rows = proj.reserves || [];
  const years = proj.years || [];

  const looksLikeVoorziening = (name) => /voorziening/i.test(String(name || ""));

  const data = years.map((y) => {
    let algemeen = 0;
    let bestemming = 0;
    let voorziening = 0;
    for (const r of rows) {
      const cat = String(r.category || "earmarked");
      const yr = (r.yearly || []).find((yy) => yy.year === y);
      const closing = yr ? Number(yr.closing || 0) : 0;
      if (cat === "general") algemeen += closing;
      else if (looksLikeVoorziening(r.name)) voorziening += closing;
      else bestemming += closing;
    }
    return {
      year: y,
      algemeen,
      bestemming,
      voorziening,
      total: algemeen + bestemming + voorziening,
    };
  });

  // Als de club geen reserves heeft ingevoerd is er niks te tonen — verberg de
  // sectie zodat we geen leeg vak laten staan tussen operationeel profiel en
  // jaarlijkse projectie.
  const hasAny = data.some((d) => d.total !== 0);
  if (!hasAny) return null;

  return (
    <div className="bg-card border-2 border-foreground p-6 heavy-shadow mb-8" data-testid="reserves-stacked-area-chart">
      <div className="stat-label">{t("Cumulatieve eindsaldi per 31/12")}</div>
      <h2 className="font-display font-black text-2xl uppercase tracking-tight mb-4">
        {t("Reserves & voorzieningen — opbouw")}
      </h2>
      <div className="h-[280px]">
        <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 500, height: 280 }}>
          <AreaChart data={data}>
            <CartesianGrid stroke="#e2e8f0" />
            <XAxis dataKey="year" />
            <YAxis tickFormatter={(v) => fmtCurrency(v)} width={80} />
            <Tooltip formatter={(v) => fmtCurrency(v)} />
            <Legend verticalAlign="bottom" content={LegendWithHint({ hint: t("Positie per 31/12 · gestapeld = totale reservepositie") })} />
            <ReferenceLine x={startYear} stroke="#94a3b8" strokeDasharray="3 3" />
            <Area
              type="monotone"
              dataKey="algemeen"
              stackId="1"
              name={t("Algemene reserve")}
              stroke="#0047FF"
              fill="#0047FF"
              fillOpacity={0.75}
            />
            <Area
              type="monotone"
              dataKey="bestemming"
              stackId="1"
              name={t("Bestemmingsreserves")}
              stroke="#00D05A"
              fill="#00D05A"
              fillOpacity={0.70}
            />
            <Area
              type="monotone"
              dataKey="voorziening"
              stackId="1"
              name={t("Voorzieningen")}
              stroke="#FF5900"
              fill="#FF5900"
              fillOpacity={0.65}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <ChartDescriptionEditor
        value={descriptionValue}
        onSave={onSaveDescription}
        dialogTitle={t("Beschrijving bij Reserves & voorzieningen — opbouw")}
        testid="chart-desc-reserves-stack"
      />
    </div>
  );
}


function ReservesProjectionPanel({ t, projection, descriptionValue = "", onSaveDescription }) {
  // Renders per-reserve balance projection across the scenario horizon plus any
  // "loopt leeg in Y" warnings the server produced. Hidden entirely when no
  // reserves are defined on the club (keeps the layout tidy for older clubs).
  if (!projection || !(projection.reserves || []).length) return null;
  const { years, reserves, warnings } = projection;
  const fmt = (v) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);

  return (
    <div className="border-2 border-foreground bg-card heavy-shadow mb-8" data-testid="reserves-projection-panel">
      <header className="p-4 border-b-2 border-foreground bg-accent/20 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="stat-label">{t("Reserves · projectie")}</div>
          <h2 className="font-display font-black text-2xl uppercase tracking-tight leading-none">
            {t("Saldi van (bestemmings)reserves")}
          </h2>
          <p className="text-xs text-muted-foreground mt-2 max-w-3xl">
            {t("Startpunt is het meest recente eindsaldo uit de historische jaren. Dotaties komen uit reserve-dotaties in het scenario; onttrekkingen uit investeringen die je markeerde als 'gefinancierd uit bestemmingsreserve'. Het jaarlijkse netto resultaat wordt automatisch bij de Algemene reserve opgeteld (winst) of afgetrokken (verlies) — bestemmingsreserves en voorzieningen blijven onveranderd tenzij je dotaties of onttrekkingen instelt.")}
          </p>
        </div>
        <HelpButton section="reserves-projectie" label="Uitleg reserves-projectie" size="sm" />
      </header>

      {(warnings || []).length > 0 && (
        <div className="p-4 border-b-2 border-destructive bg-destructive/10 space-y-2" data-testid="reserves-projection-warnings">
          {warnings.map((w, i) => (
            <div key={`${i}-${w}`} className="flex items-start gap-2 text-sm text-destructive">
              <span className="font-bold">⚠</span>
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/40">
              <th className="text-left px-4 py-2 font-display uppercase tracking-widest text-xs sticky left-0 bg-muted/40 z-10 border-r-2 border-foreground/20 min-w-[220px]">
                {t("Reserve")}
              </th>
              {years.map((y) => (
                <th key={y} className="text-right px-3 py-2 font-display uppercase tracking-widest text-xs font-black">{y}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {reserves.map((r, i) => (
              <ReserveProjectionRow key={`${r.name}-${i}`} t={t} r={r} years={years} fmt={fmt} />
            ))}
          </tbody>
        </table>
      </div>
      {onSaveDescription && (
        <div className="p-4 border-t-2 border-foreground/10">
          <ChartDescriptionEditor
            value={descriptionValue}
            onSave={onSaveDescription}
            dialogTitle={t("Beschrijving bij Reserves-projectie")}
            testid="chart-desc-reserves"
          />
        </div>
      )}
    </div>
  );
}


function ReserveSparkline({ points, trend, testid }) {
  // Kleine SVG-sparkline: sequentie van `closing` per jaar, genormaliseerd
  // op de eigen min-max. Groen bij trend > 0, rood bij < 0, grijs bij vlak.
  // Include Y0 (opening) zodat de lijn ook de vertrekpositie toont.
  if (!points || points.length < 2) return null;
  const W = 96;
  const H = 22;
  const P = 2; // padding
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = Math.max(1, max - min);
  const stepX = (W - P * 2) / (points.length - 1);
  const y = (v) => H - P - ((v - min) / range) * (H - P * 2);
  const path = points
    .map((v, i) => `${i === 0 ? "M" : "L"} ${(P + i * stepX).toFixed(1)} ${y(v).toFixed(1)}`)
    .join(" ");
  const stroke =
    trend > 0 ? "#00A84A" : trend < 0 ? "#DA1C22" : "#94a3b8"; // slate-400 fallback (concrete hex — html2canvas rendert CSS-vars niet altijd correct in PDF)
  const lastX = P + (points.length - 1) * stepX;
  const lastY = y(points[points.length - 1]);
  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      className="inline-block"
      data-testid={testid}
      aria-label="Trend sparkline"
    >
      {/* Zero-line reference (only useful when the range straddles 0) */}
      {min < 0 && max > 0 && (
        <line
          x1={P}
          x2={W - P}
          y1={y(0)}
          y2={y(0)}
          stroke="currentColor"
          strokeWidth="0.5"
          strokeDasharray="2 2"
          opacity="0.35"
        />
      )}
      <path d={path} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lastX} cy={lastY} r="1.75" fill={stroke} />
    </svg>
  );
}


function ReserveProjectionRow({ t, r, years, fmt }) {
  const byYear = Object.fromEntries(r.yearly.map((y) => [y.year, y]));
  const isGeneral = r.category === "general";
  const sparkPoints = [r.opening_balance, ...r.yearly.map((y) => y.closing)];
  const finalBalance = r.yearly.length ? r.yearly[r.yearly.length - 1].closing : r.opening_balance;
  const trend = finalBalance - r.opening_balance;
  return (
    <>
      <tr className="bg-foreground/5 border-t-4 border-foreground/20">
        <td colSpan={years.length + 1} className="px-4 py-2 sticky left-0 bg-foreground/5 z-10">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] px-1.5 py-0.5 font-bold uppercase tracking-widest ${r.category === "general" ? "bg-secondary text-secondary-foreground" : "bg-primary text-primary-foreground"}`}>
              {r.category === "general" ? t("Algemeen") : t("Bestemming")}
            </span>
            <span className="font-display uppercase font-black text-sm tracking-tight">{r.name}</span>
            <ReserveSparkline points={sparkPoints} trend={trend} testid={`reserve-sparkline-${r.name}`} />
            <span
              className={`text-[11px] font-mono-num font-bold ${trend > 0 ? "text-positive" : trend < 0 ? "text-destructive" : "text-muted-foreground"}`}
              data-testid={`reserve-trend-${r.name}`}
              title={t("Verschil eindsaldo t.o.v. startsaldo")}
            >
              {trend > 0 ? "▲ +" : trend < 0 ? "▼ −" : "="}
              {trend === 0 ? "" : fmt(Math.abs(trend))}
            </span>
            <span className="text-xs text-muted-foreground ml-2">
              {t("start")}: <b>{fmt(r.opening_balance)}</b>
              {r.min_balance !== r.opening_balance && (
                <span> · {t("min")}: <b className={r.min_balance <= 0 ? "text-destructive" : ""}>{fmt(r.min_balance)}</b></span>
              )}
              {r.empty_year && (
                <span className="ml-2 text-destructive font-bold">· {t("leeg in {y}").replace("{y}", r.empty_year)}</span>
              )}
            </span>
          </div>
        </td>
      </tr>
      {isGeneral && (
        <tr className="hover:bg-muted/20" data-testid={`reserve-proj-result-row-${r.name}`}>
          <td className="px-4 py-1.5 sticky left-0 bg-card z-10 border-r-2 border-foreground/20 text-xs text-muted-foreground pl-8">
            {t("± Netto resultaat")}
          </td>
          {years.map((y) => {
            const cell = byYear[y];
            const v = cell?.result_addition || 0;
            const cls = v > 0 ? "text-positive" : v < 0 ? "text-destructive" : "text-muted-foreground";
            return (
              <td key={y} className={`px-3 py-1.5 font-mono-num text-right text-xs ${cls}`} data-testid={`reserve-proj-result-${r.name}-${y}`}>
                {v === 0 ? "—" : (v > 0 ? `+${fmt(v)}` : `−${fmt(Math.abs(v))}`)}
              </td>
            );
          })}
        </tr>
      )}
      {isGeneral && r.yearly.some((y) => (y.outflow_to_earmarked || 0) > 0) && (
        <tr className="hover:bg-muted/20" data-testid={`reserve-proj-outflow-row-${r.name}`}>
          <td className="px-4 py-1.5 sticky left-0 bg-card z-10 border-r-2 border-foreground/20 text-xs text-muted-foreground pl-8"
              title={t("Bestemmings-dotaties worden gedekt uit de Algemene reserve — dit is de doorstroom-onttrekking die de balans sluitend maakt.")}>
            {t("− Naar bestemmingsreserves")}
          </td>
          {years.map((y) => {
            const cell = byYear[y];
            const v = cell?.outflow_to_earmarked || 0;
            return (
              <td key={y} className={`px-3 py-1.5 font-mono-num text-right text-xs ${v > 0 ? "text-destructive" : "text-muted-foreground"}`}
                  data-testid={`reserve-proj-outflow-${r.name}-${y}`}>
                {v === 0 ? "—" : `−${fmt(v)}`}
              </td>
            );
          })}
        </tr>
      )}
      <tr className="hover:bg-muted/20">
        <td className="px-4 py-1.5 sticky left-0 bg-card z-10 border-r-2 border-foreground/20 text-xs text-muted-foreground pl-8">
          {t("+ Dotatie")}
        </td>
        {years.map((y) => {
          const cell = byYear[y];
          return (
            <td key={y} className="px-3 py-1.5 font-mono-num text-right text-xs" data-testid={`reserve-proj-dot-${r.name}-${y}`}>
              {cell?.dotation > 0 ? <span className="text-positive">+{fmt(cell.dotation)}</span> : "—"}
            </td>
          );
        })}
      </tr>
      <tr className="hover:bg-muted/20">
        <td className="px-4 py-1.5 sticky left-0 bg-card z-10 border-r-2 border-foreground/20 text-xs text-muted-foreground pl-8">
          {t("− Onttrekking")}
        </td>
        {years.map((y) => {
          const cell = byYear[y];
          return (
            <td key={y} className="px-3 py-1.5 font-mono-num text-right text-xs" data-testid={`reserve-proj-wd-${r.name}-${y}`}>
              {cell?.withdrawal > 0 ? <span className="text-destructive">−{fmt(cell.withdrawal)}</span> : "—"}
            </td>
          );
        })}
      </tr>
      <tr className="hover:bg-muted/20">
        <td className="px-4 py-1.5 sticky left-0 bg-card z-10 border-r-2 border-foreground/20 text-xs font-display uppercase font-bold">
          {t("= Eindsaldo")}
        </td>
        {years.map((y) => {
          const cell = byYear[y];
          const closing = cell?.closing ?? 0;
          const cls = closing <= 0 ? "text-destructive font-black" : "font-bold";
          return (
            <td key={y} className={`px-3 py-1.5 font-mono-num text-right ${cls}`} data-testid={`reserve-proj-closing-${r.name}-${y}`}>
              {fmt(closing)}
            </td>
          );
        })}
      </tr>
    </>
  );
}


// ---- Bestaande leningen — metadata + per-jaar projectie ----
// Renders alleen wanneer de club expliciete `existing_loans` heeft; anders
// blijft de sectie leeg zodat scenario's zonder gedetailleerd debt-model niet
// verward worden. De PDF-export snapshotet dit paneel via html2canvas
// (data-testid="existing-loans-projection-panel").
function ExistingLoansProjectionPanel({ t, projection, meta, hasLoans }) {
  if (!hasLoans || !(projection || []).length) return null;
  const fmt = (v) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
  const pct = (v) => `${(Number(v) * 100).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
  // DSCR-format: 2 decimalen, kleurcodering ≥1.25 groen / 1.0-1.25 oranje / <1.0 rood.
  const fmtDscr = (v) => (v === null || v === undefined) ? "—" : Number(v).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const dscrClass = (v) => {
    if (v === null || v === undefined) return "text-muted-foreground";
    if (v < 1) return "text-destructive font-bold";
    if (v < 1.25) return "text-[#FF5900] font-bold"; // High-Vis Orange = tactical warning
    return "text-[#00D05A] font-bold"; // Tactical Green = comfort
  };

  const totalInterest = projection.reduce((s, r) => s + (Number(r.interest) || 0), 0);
  const totalPrincipal = projection.reduce((s, r) => s + (Number(r.principal) || 0), 0);
  // Jaren na de laatste betaling (alles € 0) worden weggelaten.
  let lastPaidIdx = -1;
  projection.forEach((r, i) => { if ((Number(r.interest) || 0) + (Number(r.principal) || 0) > 0) lastPaidIdx = i; });
  const visibleRows = projection.slice(0, lastPaidIdx + 1);
  const omittedYears = projection.length - visibleRows.length;

  return (
    <div className="border-2 border-foreground bg-card heavy-shadow mb-8" data-testid="existing-loans-projection-panel">
      <header className="p-4 border-b-2 border-foreground bg-accent/20 flex items-start justify-between gap-3">
        <div>
          <div className="stat-label">{t("Bestaande leningen · projectie")}</div>
          <h2 className="font-display font-black text-2xl uppercase tracking-tight leading-none">
            {t("Bestaande leningen — schedule")}
          </h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-3xl">
            {t("Rente en aflossing per jaar, exact geamortiseerd op basis van hoofdsom, rentepercentage, looptijd en startdatum van elke ingevoerde lening.")}
          </p>
        </div>
        <HelpButton section="dscr" label="Uitleg leningen en DSCR" size="sm" />
      </header>

      {(meta || []).length > 0 && (
        <div className="overflow-x-auto border-b-2 border-foreground">
          <table className="w-full text-sm" data-testid="existing-loans-meta-table">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left px-4 py-2 stat-label">{t("Naam")}</th>
                <th className="text-right px-4 py-2 stat-label">{t("Hoofdsom")}</th>
                <th className="text-right px-4 py-2 stat-label">{t("Rente")}</th>
                <th className="text-right px-4 py-2 stat-label">{t("Looptijd")}</th>
                <th className="text-left px-4 py-2 stat-label">{t("Startdatum")}</th>
                <th className="text-left px-4 py-2 stat-label">{t("Type")}</th>
              </tr>
            </thead>
            <tbody>
              {meta.map((l, i) => (
                <tr key={`${l.name}-${i}`} className="border-t border-border">
                  <td className="px-4 py-1.5 font-medium">{l.name}</td>
                  <td className="px-4 py-1.5 text-right font-mono-num">{fmt(l.principal)}</td>
                  <td className="px-4 py-1.5 text-right font-mono-num">{pct(l.interest_rate)}</td>
                  <td className="px-4 py-1.5 text-right font-mono-num">{l.term_years} {t("jr")}</td>
                  <td className="px-4 py-1.5 font-mono-num">{l.start_date}</td>
                  <td className="px-4 py-1.5">{l.loan_type === "linear" ? t("Lineair") : t("Annuïteit")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr>
              <th className="text-left px-4 py-2 stat-label sticky left-0 bg-muted/40">{t("Jaar")}</th>
              <th className="text-right px-4 py-2 stat-label">{t("Rente")}</th>
              <th className="text-right px-4 py-2 stat-label">{t("Aflossing")}</th>
              <th className="text-right px-4 py-2 stat-label">{t("Totaal")}</th>
              <th className="text-right px-4 py-2 stat-label" title={termHelp("dscr")}>{termShort("dscr")}</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((r) => {
              const total = (Number(r.interest) || 0) + (Number(r.principal) || 0);
              return (
                <tr key={r.actual_year} className="border-t border-border hover:bg-muted/20" data-testid={`exloan-proj-row-${r.actual_year}`}>
                  <td className="px-4 py-1.5 font-display uppercase font-bold sticky left-0 bg-card">{r.actual_year}</td>
                  <td className="px-4 py-1.5 text-right font-mono-num text-destructive">{r.interest > 0 ? `−${fmt(r.interest)}` : fmt(0)}</td>
                  <td className="px-4 py-1.5 text-right font-mono-num text-destructive">{r.principal > 0 ? `−${fmt(r.principal)}` : fmt(0)}</td>
                  <td className="px-4 py-1.5 text-right font-mono-num font-bold">{fmt(total)}</td>
                  <td className={`px-4 py-1.5 text-right font-mono-num ${dscrClass(r.dscr_existing)}`} data-testid={`exloan-proj-dscr-${r.actual_year}`}>
                    {fmtDscr(r.dscr_existing)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-muted/30 border-t-2 border-foreground">
            {omittedYears > 0 && (
              <tr data-testid="exloan-proj-omitted">
                <td colSpan={5} className="px-4 py-1.5 text-[11px] text-muted-foreground italic">
                  {t("Na {year} zijn alle leningen afgelost — de resterende {n} jaar (€ 0) zijn weggelaten.").replace("{year}", String(visibleRows[visibleRows.length - 1]?.actual_year ?? "")).replace("{n}", String(omittedYears))}
                </td>
              </tr>
            )}
            <tr>
              <td className="px-4 py-2 stat-label font-bold">{t("Totaal horizon")}</td>
              <td className="px-4 py-2 text-right font-mono-num font-bold" data-testid="exloan-proj-total-interest">−{fmt(totalInterest)}</td>
              <td className="px-4 py-2 text-right font-mono-num font-bold" data-testid="exloan-proj-total-principal">−{fmt(totalPrincipal)}</td>
              <td className="px-4 py-2 text-right font-mono-num font-black">{fmt(totalInterest + totalPrincipal)}</td>
              <td className="px-4 py-2"></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="px-4 py-3 border-t-2 border-foreground bg-muted/10 text-xs text-muted-foreground" data-testid="dscr-legend">
        <div className="flex flex-wrap gap-x-6 gap-y-1">
          <span><strong>{term("dscr")}</strong> = {t("beschikbare kas voor rente en aflossing (CADS) ÷ (rente + aflossing).")}</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-[#00D05A]"></span> ≥ 1,25 {t("comfort")}</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-[#FF5900]"></span> 1,00–1,25 {t("krap")}</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-destructive"></span> &lt; 1,00 {t("tekort")}</span>
        </div>
        <div className="mt-2 space-y-0.5 text-muted-foreground/90">
          <div><strong>DSCR</strong> = {t("Debt-Service Coverage Ratio — schulddekkingsgraad: hoe vaak de club haar rente en aflossing uit de kas kan betalen.")}</div>
          <div><strong>CADS</strong> = {t("Cash Available for Debt Service — beschikbare kas voor rente en aflossing (operationeel resultaat vóór afschrijvingen, min belasting).")}</div>
          <div><strong>EBITDA</strong> = {t("Earnings Before Interest, Taxes, Depreciation and Amortization — operationeel resultaat vóór rente, belasting en afschrijvingen.")}</div>
        </div>
      </div>
    </div>
  );
}

